/* eslint-disable sonarjs/cognitive-complexity */
import { CancelFunctions } from 'components/common/types';
import { OpenStreetMapPoi } from 'components/common/types/OpenStreetMapPoi.types';
import { PlannerPoi, POI, PoiOption } from 'components/common/types/Planner.types';
import { getDirectSalesPoiData, getPoiData } from 'modules/api/lookups';
import { getOpenStreetMapPoi } from 'modules/api/openStreetMapPoi';
import { cloneDeep } from 'lodash';
import store from 'store';
import { addToastNotification } from 'store/notification/reducer';
import { OPEN_STREET_MAP_DATA_PROVIDER } from '../ProgrammaticPointsOfInterest/OpenStreetMapPoiSearch/consts';
import { CUSTOM_POI_DATA_PROVIDER } from './consts';
import { transformOpenStreetMapPoiToPlannerPoi } from '../ProgrammaticPointsOfInterest/OpenStreetMapPoiSearch/utils';

export const fetchPlannerPois = async ({
  pois,
  cancelFunctions,
}: {
  pois: POI[];
  cancelFunctions: CancelFunctions;
}): Promise<POI[]> => {
  const plannerPOIs = pois.filter(
    ({ dataProvider }) =>
      dataProvider !== OPEN_STREET_MAP_DATA_PROVIDER.value && dataProvider !== CUSTOM_POI_DATA_PROVIDER,
  );

  if (!plannerPOIs.length) return [];

  try {
    const poiPoints: PlannerPoi[] = await getPoiData(
      cancelFunctions,
      plannerPOIs.map(({ code, name }) => `${name}-${code}`),
    );

    return plannerPOIs.map((data) => {
      const assignablePoiPoints = poiPoints.flatMap((point) => {
        if (point.properties.categoryName !== data.name) return [];

        return {
          ...point,
          properties: {
            ...point.properties,
            visibility: {
              ...point.properties.visibility,
              include: data.include,
              radius: data.radius,
            },
          },
        };
      });

      return {
        ...data,
        poiPoints: assignablePoiPoints,
      };
    });
  } catch (error) {
    return [];
  }
};

export const fetchDirectSalesPois = async ({
  cancelFunctions,
  campaignId,
  dealLineId,
  marketId,
}: {
  cancelFunctions: CancelFunctions;
  campaignId: string;
  dealLineId: string;
  marketId: string;
}): Promise<PlannerPoi[]> => {
  try {
    return await getDirectSalesPoiData(cancelFunctions, marketId, campaignId, dealLineId);
  } catch (error) {
    return [];
  }
};

export const fetchOpenStreetMapPois = async ({
  pois,
  openStreetMapCountryNames,
}: {
  pois: POI[];
  openStreetMapCountryNames: string[];
}): Promise<POI[]> => {
  const openStreetMapPOIs = pois.filter(({ dataProvider }) => dataProvider === OPEN_STREET_MAP_DATA_PROVIDER.value);

  if (!openStreetMapPOIs.length) return [];

  try {
    const openStreetMapData: OpenStreetMapPoi[] = await getOpenStreetMapPoi(
      openStreetMapPOIs[0].name,
      openStreetMapCountryNames,
    );

    if (!openStreetMapData) return [];

    return [
      transformOpenStreetMapPoiToPlannerPoi(openStreetMapData, openStreetMapPOIs[0].name, openStreetMapPOIs[0].radius),
    ];
  } catch (error) {
    if (error.status === 429) {
      const { dispatch } = store;
      dispatch(
        addToastNotification({
          description: 'Too many Open Street Map POIs requested. Please try again later.',
          type: 'Failure',
        }),
      );
    }
    return [];
  }
};

const groupSelectionCategoryPaths = (values: POI[]): { included: Set<string>; excluded: Set<string> } => {
  return values.reduce(
    (acc: { included: Set<string>; excluded: Set<string> }, curr: POI) => {
      if (curr.include) acc.included.add(curr.categoryPath);
      else if (curr.include === false) acc.excluded.add(curr.categoryPath);
      return acc;
    },
    { included: new Set(), excluded: new Set() },
  );
};

const recursiveInclusionFilter = (poiOption: PoiOption, selectedPOIs: Set<string>): void => {
  const allowedChildren: PoiOption[] = [];

  if (poiOption.children && !(poiOption.categoryPath && selectedPOIs.has(poiOption.categoryPath))) {
    poiOption.children.forEach((child) => {
      if (child.categoryPath && selectedPOIs.has(child.categoryPath)) {
        allowedChildren.push(child);
        return;
      }

      if (child.children?.length) {
        const allowed = child.children.filter((grandChild) => {
          if (grandChild.categoryPath && selectedPOIs.has(grandChild.categoryPath)) return grandChild;

          if (grandChild.children?.length) return recursiveInclusionFilter(grandChild, selectedPOIs);

          return null;
        });

        if (allowed.length) allowedChildren.push({ ...child, children: allowed });
      }
    });

    poiOption.children = allowedChildren;
  }
};

const isCategoryExcluded = (
  child: PoiOption,
  excludedFilterPOIs: Set<string>,
  excludedObjectivePOIs: Set<string>,
): boolean => {
  return Boolean(
    child.categoryPath && excludedFilterPOIs.has(child.categoryPath) && !excludedObjectivePOIs.has(child.categoryPath),
  );
};

const recursiveExclusionFilter = (
  poiOption: PoiOption,
  excludedFilterPOIs: Set<string>,
  excludedObjectivePOIs: Set<string>,
): void => {
  if (!poiOption.categoryPath) return;

  const isExcluded = isCategoryExcluded(poiOption, excludedFilterPOIs, excludedObjectivePOIs);
  const allowedChildren: PoiOption[] = [];

  if (poiOption.children && isExcluded) {
    poiOption.children = [];
    return;
  }

  if (poiOption.children && !isExcluded) {
    poiOption.children.forEach((child) => {
      if (!isCategoryExcluded(child, excludedFilterPOIs, excludedObjectivePOIs) && child.children) {
        const allowed = child.children.filter((grandChild) => {
          const isAllowed = grandChild.categoryPath && !excludedFilterPOIs.has(grandChild.categoryPath);

          if (isAllowed) return grandChild;

          if (grandChild.children?.length) {
            return recursiveExclusionFilter(grandChild, excludedFilterPOIs, excludedObjectivePOIs);
          }

          return null;
        });

        if (allowed.length) allowedChildren.push({ ...child, children: allowed });
      }
    });

    poiOption.children = allowedChildren;
  }
};

export const filterSelectedPoiOptions = ({
  poiOptions = [],
  selectedPOIs = [],
  selectedFilterPOIs = [],
}: {
  poiOptions: PoiOption[];
  selectedPOIs: POI[];
  selectedFilterPOIs: POI[];
}): PoiOption[] => {
  if (!selectedFilterPOIs.length && !selectedPOIs.length) return poiOptions;

  const { included: includedPOIs, excluded: excludedPOIs } = groupSelectionCategoryPaths(selectedPOIs);
  const { included: includedFilterPOIs, excluded: excludedFilterPOIs } =
    groupSelectionCategoryPaths(selectedFilterPOIs);

  if (!includedFilterPOIs.size && !excludedFilterPOIs.size) return poiOptions;

  const poiOptionsClone = cloneDeep(poiOptions);

  if (includedFilterPOIs.size) {
    poiOptionsClone.forEach((poiOption) =>
      recursiveInclusionFilter(poiOption, new Set([...includedPOIs, ...includedFilterPOIs, ...excludedPOIs])),
    );
  } else if (excludedFilterPOIs.size) {
    poiOptionsClone.forEach((poiOption) => recursiveExclusionFilter(poiOption, excludedFilterPOIs, excludedPOIs));
  }

  return poiOptionsClone.filter((poiOption) => poiOption.children?.length);
};
/* eslint-enable sonarjs/cognitive-complexity */
